import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Button,
  Checkbox,
  CircularProgress,
  Fab,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Close, Search } from '@mui/icons-material'
import './SupplierCatalogPage.css'
import productSearchEmptyState from '../TdSynnex/product-search-empty-state.png'
import SupplierPartDrawer from '../SupplierPartDrawer'
import DistributorAddToQuoteDialog from '../DistributorAddToQuoteDialog'
import { SupplierCatalogConfig } from './supplierCatalogTypes'
import { SUPPLIER_CATALOG_ROWS_PER_PAGE_OPTIONS, useSupplierCatalogSearch } from './useSupplierCatalogSearch'

function selectionByLabel<T>(selected: Map<string | number, T>, partNumber: (row: T) => string) {
  const byLabel: Record<string, T> = {}
  for (const row of selected.values()) {
    let label = partNumber(row)
    for (let n = 2; label in byLabel; n++) label = `${partNumber(row)} (${n})`
    byLabel[label] = row
  }
  return byLabel
}

export default function SupplierCatalogPage<T>({ config }: { config: SupplierCatalogConfig<T> }) {
  const [searchParams] = useSearchParams()
  const initialTerm = searchParams.get('pn')?.trim() ?? ''
  const [term, setTerm] = useState(initialTerm)
  const [selectedRow, setSelectedRow] = useState<T | null>(null)
  const [shownRow, setShownRow] = useState<T | null>(null)
  const [selected, setSelected] = useState(new Map<string | number, T>())
  const [addToQuoteOpen, setAddToQuoteOpen] = useState(false)
  const {
    requestedTerm,
    rows,
    total,
    page,
    rowsPerPage,
    loading,
    errorMessage,
    search,
    changePage,
    changeRowsPerPage,
    cancel,
    dismissError,
  } = useSupplierCatalogSearch<T>(config.endpoint, config.supplier, initialTerm)

  if (selectedRow && selectedRow !== shownRow) setShownRow(selectedRow)

  useEffect(() => {
    document.title = `${config.supplier} Catalog | I-Quote`
  }, [config.supplier])

  useEffect(() => {
    setSelected(new Map())
  }, [requestedTerm])

  const trimmedTerm = term.trim()
  const canSearch = trimmedTerm.length >= 2 || (trimmedTerm === '' && requestedTerm !== '')
  const handleSearch = () => {
    if (!canSearch) return
    search(term)
  }
  const handleClear = () => {
    setTerm('')
    if (requestedTerm !== '' || loading === 'loading') search('')
  }

  const toggleRow = (row: T) =>
    setSelected(prev => {
      const next = new Map(prev)
      const key = config.rowKey(row)
      if (next.has(key)) next.delete(key)
      else next.set(key, row)
      return next
    })
  const pageSelectedCount = rows?.filter(row => selected.has(config.rowKey(row))).length ?? 0
  const togglePage = () =>
    setSelected(prev => {
      const next = new Map(prev)
      const selectAll = pageSelectedCount < (rows?.length ?? 0)
      for (const row of rows ?? []) {
        if (selectAll) next.set(config.rowKey(row), row)
        else next.delete(config.rowKey(row))
      }
      return next
    })
  const selectedByLabel = selectionByLabel(selected, config.partNumber)

  return (
    <div className="supplier-catalog-page">
      <div className="supplier-catalog-view">
        <div className="supplier-catalog-search-toolbar">
          <Typography variant="h6" className="supplier-catalog-toolbar-label">
            Search {config.supplier} by Part #, manufacturer or description
          </Typography>
          <div className="supplier-catalog-search-row">
            <TextField
              fullWidth
              size="small"
              placeholder="Type a part number, manufacturer, or description"
              value={term}
              onChange={e => setTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSearch()
                else if (e.key === 'Escape' && loading === 'loading') cancel()
              }}
              slotProps={{
                input: {
                  endAdornment: term ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={handleClear}>
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Search />}
              disabled={!canSearch}
              loading={loading === 'loading'}
              onClick={handleSearch}
            >
              Search
            </Button>
          </div>
        </div>

        {rows === null ? (
          <div className="supplier-catalog-empty-state">
            {loading === 'loading' ? (
              <CircularProgress aria-label={`Loading the ${config.supplier} catalog`} />
            ) : (
              <>
                <Typography>The {config.supplier} catalog didn't load.</Typography>
                <Button variant="outlined" onClick={() => search(term)}>
                  Try again
                </Button>
              </>
            )}
          </div>
        ) : rows.length === 0 ? (
          <div className="supplier-catalog-empty-state">
            <img src={productSearchEmptyState} alt="" className="supplier-catalog-empty-state-icon" />
            <Typography>
              {requestedTerm ? `No results found for ${requestedTerm}` : `The ${config.supplier} catalog is empty`}
            </Typography>
          </div>
        ) : (
          <div className="supplier-catalog-results">
            <div className="supplier-catalog-results-header">
              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={SUPPLIER_CATALOG_ROWS_PER_PAGE_OPTIONS}
                onPageChange={(_event, nextPage) => changePage(nextPage)}
                onRowsPerPageChange={e => changeRowsPerPage(parseInt(e.target.value, 10))}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`
                }
                disabled={loading === 'loading'}
              />
            </div>
            <TableContainer className="supplier-catalog-table-container">
              <Table size="small" stickyHeader>
                <TableHead className="supplier-catalog-table-header">
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={pageSelectedCount > 0 && pageSelectedCount === rows.length}
                        indeterminate={pageSelectedCount > 0 && pageSelectedCount < rows.length}
                        onChange={togglePage}
                        inputProps={{ 'aria-label': 'Select all rows on this page' }}
                      />
                    </TableCell>
                    <TableCell className="supplier-catalog-col-supplier-part">Supplier Part #</TableCell>
                    {config.columns.map(column => (
                      <TableCell key={column.label} className={column.className} align={column.align}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow hover key={config.rowKey(row)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(config.rowKey(row))}
                          onChange={() => toggleRow(row)}
                          inputProps={{ 'aria-label': `Select ${config.partNumber(row)}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          className="supplier-catalog-part-link"
                          onClick={() => setSelectedRow(row)}
                        >
                          {config.partNumber(row)}
                        </Link>
                      </TableCell>
                      {config.columns.map(column => (
                        <TableCell key={column.label} align={column.align}>
                          {column.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        <SupplierPartDrawer
          open={selectedRow !== null}
          onClose={() => setSelectedRow(null)}
          title={shownRow ? config.partNumber(shownRow) : `${config.supplier} part`}
          supplierLabel={config.supplier}
          status="found"
          fields={shownRow ? config.drawerFields(shownRow) : null}
        />
        <DistributorAddToQuoteDialog
          open={addToQuoteOpen}
          onClose={() => setAddToQuoteOpen(false)}
          partNumbers={Object.keys(selectedByLabel)}
          results={selectedByLabel}
          itemToLineItem={(lineNumber, _label, row) => config.toLineItem(lineNumber, row)}
        />
        <Snackbar autoHideDuration={5000} open={loading === 'error'} onClose={dismissError} message={errorMessage} />
      </div>

      <Fab
        variant="extended"
        color="primary"
        size="large"
        disabled={selected.size === 0}
        onClick={() => setAddToQuoteOpen(true)}
      >
        Add to Quote{selected.size > 0 ? ` (${selected.size})` : ''}
      </Fab>
    </div>
  )
}
